/**
 * The six papers a sticky note comes in (SRS C4).
 *
 * Held by name — 'yellow', 'pink' — rather than as a colour, because that is what the
 * data model stores (§3.3) and because the paper is then free to be defined once, in
 * the stylesheet, alongside every other colour in the product.
 *
 * The hex beside each name is the fallback for the moment before the stylesheet has
 * applied and for tests, which run with no CSS at all. The token always wins when one
 * is available — exactly as the pen palette works.
 */

import { STICKY_COLORS, type StickyColor } from '@opal/shared';

import { cssColor } from './palette.js';

export interface StickyPaper {
  readonly value: StickyColor;
  readonly token: string;
  readonly fallback: string;
  /** Said aloud by a screen reader, and shown in the swatch's tooltip. */
  readonly name: string;
}

export const STICKY_PAPERS: readonly StickyPaper[] = [
  { value: 'yellow', token: '--sticky-yellow', fallback: '#ffe9a8', name: 'Yellow' },
  { value: 'pink', token: '--sticky-pink', fallback: '#ffd3d8', name: 'Pink' },
  { value: 'orange', token: '--sticky-orange', fallback: '#ffdcc2', name: 'Orange' },
  { value: 'green', token: '--sticky-green', fallback: '#c9edcb', name: 'Green' },
  { value: 'blue', token: '--sticky-blue', fallback: '#cbe4fa', name: 'Blue' },
  { value: 'violet', token: '--sticky-violet', fallback: '#ded6fb', name: 'Violet' },
];

/** The paper a sticky note starts on. */
export const DEFAULT_STICKY_COLOR: StickyColor = STICKY_COLORS[0] ?? 'yellow';

/** The ink on every sticky, whatever colour the paper is. */
const INK_TOKEN = '--sticky-ink';
const INK_FALLBACK = '#1a1a2e';

/** The line around the paper, so a pale note still has an edge on white board. */
const EDGE_TOKEN = '--sticky-edge';
const EDGE_FALLBACK = 'rgba(26, 26, 46, 0.12)';

function paperOf(color: StickyColor): StickyPaper | undefined {
  return STICKY_PAPERS.find((paper) => paper.value === color);
}

/** The paper colour, as something canvas can paint with. */
export function stickyFill(color: StickyColor): string {
  const paper = paperOf(color);
  return cssColor(paper?.token ?? '--sticky-yellow', paper?.fallback ?? '#ffe9a8');
}

/** The paper colour, as a CSS value for a swatch or the editor to show itself in. */
export function stickySwatch(color: StickyColor): string {
  return `var(${paperOf(color)?.token ?? '--sticky-yellow'})`;
}

/** The colour the words on a sticky are written in. */
export function stickyInk(): string {
  return cssColor(INK_TOKEN, INK_FALLBACK);
}

/** The colour of the line around the paper. */
export function stickyEdge(): string {
  return cssColor(EDGE_TOKEN, EDGE_FALLBACK);
}
